/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { baseComponentManager } from '../../../../registry/Components';
import { toolTipProps } from '../../../../utils/commonProps';
import { defaultValue } from './AreaToolbarEdit';

const AreaToolbarEditManager: ComponentManagerFn<'AreaToolbarEdit'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AreaToolbarEdit'>(platform, 'AreaToolbarEdit'),
    type: 'AreaToolbarEdit',
    name: 'Edit',
    defaultValue,
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      ...toolTipProps,
    },
    category: 'IoT',
    icon: { iconName: 'draw-polygon' },
    description: `**Overview**<br/>
The Area Toolbar Edit component is a specific button control placed inside the **Area Toolbar**. It toggles the "Edit Mode" of the **IoT Image Mapper**, allowing users to draw new polygon zones, resize existing ones, or drag shapes across the canvas.
Use this component to give administrators or power users the ability to modify the spatial definitions of your map directly in the browser.

**1. Functionality**<br/>
* **Toggle Behavior:** Clicking this button switches the map canvas from **View Mode** (interactive but geometry is locked) to **Edit Mode** (drawing tools and vertices become active).
* **Visual Icon:** Renders as a standard "Draw Polygon" or "Pencil" icon within the toolbar, signaling to the user that the layout can be modified.

**2. Permissions & Control**<br/>
You can restrict who has access to editing capabilities to prevent unauthorized changes to facility layouts.
* **Disable Condition:** A boolean expression used to lock the editing feature.
* **Usage:** If this expression returns **true**, the Edit button is disabled (greyed out).
* **Example:** \`$.user.role !== 'FacilityManager'\` ensures only managers can edit the floor plan, while general employees can only view it.
* **Tooltip:** Supports standard tooltip properties to show helper text (e.g., "Toggle Edit Mode") on hover.

**3. Best Use Cases**<br/>
* **Facility Management:** Allowing authorized staff to redraw room boundaries when office layouts change or temporary walls are moved.
* **Zone Definition:** Enabling warehouse supervisors to designate new storage zones or "Hot Zones" on a floor schematic as inventory needs evolve.
* **Interactive Mapping:** Updating interactive kiosks where specific territories or display areas change seasonally.`,
  };
};

export default AreaToolbarEditManager;
